import { TwinMessage } from 'api/model'
import { useActiveOrganization } from './use-organization'
import { useLayoutEffect, useState } from 'react'
import { twinsMessages } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { messagesByConversationFamily, myTwinMessagesAtom } from 'state/messages'
import { useRecoilState } from 'recoil'

function useFetchMyTwinMessages(twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [myTwinMessages, setMyTwinMessages] = useRecoilState<TwinMessage[]>(myTwinMessagesAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !twinId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsMessages(activeOrganization, twinId)

        setMyTwinMessages(response.data ? response.data : [])
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setMyTwinMessages, twinId])

  return {
    isLoading,
    myTwinMessages,
    error,
    setMyTwinMessages,
  }
}

// used in the context of an anonymous user
function useFetchAnonymousConversationMessages(twinOrgId: string, twinId: string, conversationId: string) {
  const [conversationMessages, setConversationMessages] = useRecoilState<TwinMessage[]>(
    messagesByConversationFamily(conversationId)
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!twinOrgId || !twinId || !conversationId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsMessages(twinOrgId, twinId, {
          conversation_id: conversationId,
        })

        setConversationMessages(response.data ? response.data : [])
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [twinOrgId, setConversationMessages, twinId, conversationId])

  return {
    isLoading,
    conversationMessages,
    error,
    setConversationMessages,
  }
}

type FetchConversationMessagesOptions = {
  twinOrgId?: string
  isCreatingNewConversation?: boolean
}

function useFetchConversationMessages(
  twinId: string,
  conversationId: string,
  options: FetchConversationMessagesOptions = {}
) {
  const [activeOrganization] = useActiveOrganization()
  const [conversationMessages, setConversationMessages] = useRecoilState<TwinMessage[]>(
    messagesByConversationFamily(conversationId)
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (
        (!activeOrganization && !options.twinOrgId) ||
        !twinId ||
        !conversationId ||
        options.isCreatingNewConversation // we don't want to be fetching messages when still in the process of creating a new conversation
      ) {
        return
      }

      setIsLoading(true)

      try {
        // case for fetching messages for an authenticated user
        if (activeOrganization) {
          const response = await twinsMessages(activeOrganization, twinId, {
            conversation_id: conversationId,
          })

          setConversationMessages(response.data ? response.data : [])
        } else if (options.twinOrgId) {
          // case for fetching messages for an anonymous user
          const response = await twinsMessages(options.twinOrgId, twinId, {
            conversation_id: conversationId,
          })

          setConversationMessages(response.data ? response.data : [])
        }
      } catch (error) {
        setError(`${error}`)
      } finally {
        // add a timeout here to prevent the loading indicator from flashing
        setTimeout(() => {
          setIsLoading(false)
        }, 2000)
      }
    }

    if (conversationMessages.length === 0) {
      fetchData()
    }

    // if there is no conversationId, clear the messages
    if (!conversationId) {
      setConversationMessages([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeOrganization,
    setConversationMessages,
    twinId,
    conversationId,
    options.twinOrgId,
    options.isCreatingNewConversation,
  ])

  return {
    isLoading,
    conversationMessages,
    error,
    setConversationMessages,
  }
}

export { useFetchConversationMessages, useFetchAnonymousConversationMessages, useFetchMyTwinMessages }
